import { Select, Input } from 'antd';
import React, { useEffect, useState } from 'react';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import useXmlPattern from '../../utils/xmlPattern';

const { Option } = Select;

const useFields = (t, customer) => {
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [enums, setEnums] = useState({});
  const [firstName, setFirstName] = useState('');
  const [freezeStatus, setFreezeStatus] = useState(false);
  const [freezeList, setFreezeList] = useState([]);

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/insurers/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getFreezes = async (search) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/freezes/${firstName}_${search}?limit=3`
      });
      if (data.length > 0) {
        setFreezeStatus(true);
        setFreezeList(
          data.map((f) => {
            if (f.first_name)
              return `${f.first_name} ${f.last_name.toUpperCase()}`;
            return f.last_name.toUpperCase();
          })
        );
      } else {
        setFreezeStatus(false);
        setFreezeList([]);
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const handleNameChange = async (event) => {
    const { value } = event.target;

    if (value.length > 3) {
      await getFreezes(value);
    }
  };

  useEffect(() => {
    (async () => {
      await getEnums();
    })();
    // eslint-disable-next-line
  }, []);

  return [
    {
      name: ['gender'],
      rules: [{ required: true }],
      input: (
        <Select>
          {enums.genders &&
            enums.genders.map((g) => (
              <Option value={g} key={g}>
                {t(`insurers.form.${g}`)}
              </Option>
            ))}
        </Select>
      ),
      label: customer && t('customers.form.gender'),
      content:
        customer && customer.gender && t(`insurers.form.${customer.gender}`)
    },
    {
      name: ['first_name'],
      rules: [{ required: true }, ...useXmlPattern()],
      label: customer && t('customers.form.first_name'),
      content: customer && customer.first_name,
      input: <Input onChange={(event) => setFirstName(event.target.value)} />
    },
    {
      name: ['last_name'],
      rules: [{ required: true }, ...useXmlPattern()],
      label: customer && t('customers.form.last_name'),
      content: customer && customer.last_name,
      input: <Input onChange={handleNameChange} />,
      hasFeedback: freezeStatus,
      layout: freezeStatus && {
        validateStatus: 'error',
        help: `${t('customers.form.potential_freeze')} (${freezeList.join(
          ', '
        )})`
      }
    },
    {
      name: ['email'],
      tooltip: true,
      rules: [
        { required: true },
        {
          type: 'email',
          message: t('errors.form.EMAIL')
        }
      ],
      label: customer && t('customers.form.email'),
      content: customer && customer.email && (
        <a href={`mailto:${customer.email}`} target="blank">
          {customer.email}
        </a>
      )
    },
    {
      rules: [{ required: true }, { len: 10 }],
      name: ['phone_number'],
      label: customer && t('insurers.form.customer_phone_number'),
      content: customer && (
        <a href={`tel:${customer.phone_number}`}>{customer.phone_number}</a>
      )
    },
    {
      name: ['company_name'],
      rules: [{ required: true }, ...useXmlPattern()],
      label: customer && t('contracts.form.company_name'),
      content: customer && customer.company_name,
      span: 3
    },
    {
      name: ['siret'],
      rules: [{ required: true }],
      label: customer && t('insurers.list.column.siret_number'),
      content: customer && customer.siret && (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`https://www.societe.com/cgi-bin/search?champs=${customer &&
            customer.siret}`}
        >
          {customer && customer.siret}
        </a>
      ),
      span: 1
    },
    {
      name: ['address', 'number'],
      rules: [{ required: true }],
      label: customer && t('customers.form.address.number'),
      content: customer && `${customer.address.number} `,
      span: 1
    },
    {
      name: ['address', 'street'],
      rules: [{ required: true }],
      label: customer && t('customers.form.address.street'),
      content: customer && `   ${customer.address.street}`,
      span: 1
    },
    {
      name: ['address', 'postal_code'],
      rules: [{ required: true }],
      label: customer && t('customers.form.address.postal_code'),
      content: customer && `${customer.address.postal_code}`,
      span: 1
    },
    {
      name: ['address', 'city'],
      rules: [{ required: true }],
      label: customer && t('customers.form.address.city'),
      content: customer && `${customer.address.city}`,
      span: 1
    }
  ];
};

export default useFields;
